import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';

import isEmpty from 'lodash/isEmpty';

import { AiAuthorizationErrorsList } from '@lumapps/ai/components/AiAuthorizationErrorsList';
import { AskAiFollowUpQuestions } from '@lumapps/ask-ai-conversation/components/AskAiFollowUpQuestions';
import { AskAiToggleButton } from '@lumapps/ask-ai-conversation/components/AskAiToggleButton';
import { AskAiRule } from '@lumapps/ask-ai-rules/types';
import { BaseApiError } from '@lumapps/base-api';
import { useClassnames } from '@lumapps/classnames';
import { get as getConstants } from '@lumapps/constants';
import { AppId } from '@lumapps/constants/app';
import { SeeMoreFade } from '@lumapps/lumx-layouts/components/SeeMoreFade';
import { Alignment } from '@lumapps/lumx/react';
import { ErrorBoundary } from '@lumapps/utils/components/ErrorBoundary';

import { ASK_AI_MAX_HEIGHT, ASK_AI_MAX_HEIGHT_PREVIEW, ASK_AI_MAX_HEIGHT_WEBVIEW } from '../../constants';
import type { AskAiFeatures, AskSearchBestAnswer } from '../../types';
import { askAnswerHasAuthError } from '../../utils';
import { AskAiDisclaimer } from '../AskAiDisclaimer';
import { AskAiEmptyState } from '../AskAiEmptyState';
import { AskAiErrorState } from '../AskAiErrorState';
import { AskAiResultContent } from '../AskAiResultContent';

import './index.scss';

const CLASSNAME = 'ask-ai';

export interface AskAiProps {
    /** The query from which the answer has been generated. */
    query: string;
    /** The answer generated to display. */
    bestAnswer?: AskSearchBestAnswer;
    /** Classname to set on the wrapper container */
    className?: string;
    /** Error received on Ask AI query. */
    error?: BaseApiError | null;
    /** Unique ID to identify each search */
    traceId: string;
    /** Whether the component is used in the micro-apps builder context, only for preview */
    isPreview?: boolean;
    /** Method used to remake an Ask AI query */
    refetch?: () => void;
    /** All features state. Activated or not */
    features?: AskAiFeatures;
    /** Whether the Ask Answer is currently streaming */
    isStreaming?: boolean;
    /** Handle change agentId */
    handleAgentChange?: (rule: AskAiRule) => void;
    /** Selected agent id - selected by the user */
    selectedAgent?: AskAiRule;
    /** Whether the Ask Answer is currently loading */
    isLoading?: boolean;
}

/**
 * Display the result given by the `useAskAi` hook.
 */
export const AskAi: React.FC<AskAiProps> = ({
    className,
    query,
    bestAnswer,
    error,
    traceId,
    isPreview = false,
    refetch,
    features = {
        isAgentEnabled: false,
        isConversationalEnabled: false,
    },
    isStreaming,
    handleAgentChange,
    selectedAgent,
    isLoading,
}) => {
    const { block, element } = useClassnames(CLASSNAME);

    const { applicationId } = getConstants();
    const isWebview = applicationId === AppId.webview;

    /**
     * Create a combined seed that changes when either concatenatedChunks, isStreaming or isActionableItemLoaded changes
     * Change it in a `useLayoutEffect` to calculate the seed before updating the DOM and avoid flickers
     */
    const recalculationSeed = useRef<string>('');
    useLayoutEffect(() => {
        recalculationSeed.current = `${bestAnswer?.answer}-${isStreaming}`;
    }, [bestAnswer?.answer, isStreaming]);

    const noResults = isEmpty(bestAnswer?.answer) && !isStreaming && !isLoading;

    const hasAuthErrors = bestAnswer && askAnswerHasAuthError(bestAnswer);

    const isError = !!error && !isEmpty(error);

    // Show follow-up questions input only after the streaming is done
    const shouldShowFollowUpQuestions = features.isConversationalEnabled && bestAnswer?.answer && !isStreaming;

    const [isFollowUpOpen, setIsFollowUpOpen] = useState(false);

    /**
     * Sets default SeeMoreFade max height depending on context
     * - If we're in micro-app builder: `ASK_AI_MAX_HEIGHT_PREVIEW`
     * - Else: default `ASK_AI_MAX_HEIGHT`
     */
    const seeMoreMaxHeight = useMemo(() => {
        if (isPreview) {
            return ASK_AI_MAX_HEIGHT_PREVIEW;
        }

        if (isWebview) {
            return ASK_AI_MAX_HEIGHT_WEBVIEW;
        }
        return ASK_AI_MAX_HEIGHT;
    }, [isPreview, isWebview]);

    if (isError) {
        return <AskAiErrorState status={error.status} />;
    }

    if (!isError && noResults) {
        return <AskAiEmptyState />;
    }

    const isLoadingAfterAgentSelection = isLoading && selectedAgent;

    /** Handle the agent change */
    const handleSelectedAgentChange = (rule: AskAiRule) => {
        /** Should close follow-up questions if the agent is changed */
        setIsFollowUpOpen(false);
        /** Set the selected agent value */
        handleAgentChange?.(rule);
    };

    return (
        <article
            className={block(
                {
                    'vertical-center': Boolean(isError || noResults),
                    'has-follow-up': Boolean(isFollowUpOpen),
                },
                className,
            )}
        >
            <SeeMoreFade
                maxHeight={seeMoreMaxHeight}
                recalculationSeed={recalculationSeed.current}
                vAlign={Alignment.center}
                hAlign={Alignment.top}
                orientation="vertical"
                renderToggleButton={(toggleDisplay, isMoreOpen) =>
                    !isFollowUpOpen &&
                    !(isLoadingAfterAgentSelection || isStreaming) && (
                        <AskAiToggleButton
                            onClick={toggleDisplay}
                            isMoreOpen={isMoreOpen}
                            isConversationalEnabled={features.isConversationalEnabled}
                        />
                    )
                }
                previewOnHover={features.isConversationalEnabled}
            >
                <div className={element('result')}>
                    <div className={element('result-details')}>
                        <AskAiDisclaimer isHumanCurated={bestAnswer?.isHumanCurated} isWebview={isWebview} />
                        <AskAiResultContent
                            className={element('value')}
                            answer={bestAnswer || {}}
                            originalQuery={query}
                            traceId={traceId}
                            isPreview={isPreview}
                            isStreaming={isStreaming}
                            isLoading={(isStreaming || isLoadingAfterAgentSelection) && !bestAnswer?.answer}
                            shouldDisplayAgentName={!isPreview && features.isAgentEnabled}
                            handleAgentChange={handleSelectedAgentChange}
                        />

                        {hasAuthErrors && (
                            <AiAuthorizationErrorsList authErrors={bestAnswer.authErrors} refetch={refetch} />
                        )}

                        {shouldShowFollowUpQuestions && !isWebview && (
                            <ErrorBoundary>
                                <AskAiFollowUpQuestions
                                    isOpen={isFollowUpOpen}
                                    setIsOpen={setIsFollowUpOpen}
                                    initialTraceId={traceId}
                                    initialSuggestedQuestions={bestAnswer?.suggestedFollowUpQuestions}
                                    conversationId={bestAnswer?.conversationId}
                                    isInitialResultStreaming={isStreaming}
                                    initialSelectedAgent={selectedAgent}
                                />
                            </ErrorBoundary>
                        )}
                    </div>
                </div>
            </SeeMoreFade>
        </article>
    );
};
